import { motion } from "framer-motion";

/**
 * AnimatedFeatureIcon — takes a Lucide `icon` component and a semantic
 * `type` and returns the icon wrapped in a subtle, on-brand animation.
 * Motion is looping, calming, and never game-like. Every tool card in
 * Drawing / Language / Math uses this component so their icons feel
 * alive without adding any GIF asset weight.
 *
 * Extending: add a new key to `ANIMATIONS`. If a type has no entry we
 * fall back to a gentle float-bounce so nothing looks accidentally static.
 */

const ease = "easeInOut";

const ANIMATIONS = {
  // Drawing Tools
  pencil:   { animate: { rotate: [-8, 8, -8, 8, -8], y: [0, -1, 0, -1, 0] }, duration: 3.2 },
  coloring: { animate: { rotate: [0, 360] },                                  duration: 14, ease: "linear" },
  "colour-guide": { animate: { rotate: [-8, 8, -8], scale: [1, 1.08, 1] },     duration: 2.6 },
  story:    { animate: { rotateY: [0, 180, 360] },                            duration: 6 },

  // Language Tools
  summary:         { animate: { scale: [1, 1.08, 1], rotate: [-3, 3, -3] },   duration: 2.8 },
  "speech-mentor": { animate: { scale: [1, 1.14, 1] },                        duration: 1.8 },
  mcq:             { animate: { y: [0, -3, 0, -3, 0], rotate: [-2, 2, -2] },  duration: 2.4 },
  "check-answer":  { animate: { rotate: [-6, 6, -6], scale: [1, 1.05, 1] },   duration: 2.6 },
  "learn-nature":  { animate: { rotate: [-10, 6, -10], y: [0, -2, 0] },       duration: 3.8 },

  // Math Tools
  hint:          { animate: { opacity: [0.7, 1, 0.7], scale: [1, 1.12, 1] },       duration: 1.9 },
  solution:      { animate: { rotate: [-4, 4, -4], y: [0, -2, 0] },                duration: 2.6 },
  "mental-math": { animate: { scale: [1, 1.08, 1], rotate: [-3, 3, -3] },          duration: 2.8 },
};

const FALLBACK = { animate: { y: [0, -3, 0], scale: [1, 1.04, 1] }, duration: 2.6 };

export default function AnimatedFeatureIcon({
  type,
  icon: Icon,
  size = 32,
  strokeWidth = 2.5,
  className = "text-slate-800",
}) {
  const a = ANIMATIONS[type] || FALLBACK;
  return (
    <motion.div
      style={{ display: "inline-flex", transformOrigin: "50% 60%" }}
      animate={a.animate}
      transition={{ duration: a.duration, repeat: Infinity, ease: a.ease || ease }}
    >
      <Icon size={size} strokeWidth={strokeWidth} className={className} />
    </motion.div>
  );
}
